import React, { useMemo, useState } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { WavyDivider } from '../components/WavyDivider';
import { useSeo } from '../lib/useSeo';
import { BlogCard } from '../components/BlogCard';
import { blogPosts, categories, BlogCategory } from '../data/blog';
type FilterValue = 'all' | BlogCategory;
export function Blog() {
  const [activeCategory, setActiveCategory] = useState<BlogCategory | 'all'>('all');
  useSeo({
    title: 'Blog & Reflections',
    description: "Read Milangela's latest thoughts on mindful living, energy tuning, and the simple joys of nature.",
    path: '/blog',
    image: 'https://images.unsplash.com/photo-1500964757637-c85e8a162699?auto=format&fit=crop&q=80'
  });
  const sortedPosts = useMemo(() => [...blogPosts].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()), []);
  const filteredPosts = useMemo(() => {
    if (activeCategory === 'all') return sortedPosts;
    return sortedPosts.filter(p => p.category === activeCategory);
  }, [activeCategory, sortedPosts]);
  const [featured, ...rest] = filteredPosts;
  return <div className="min-h-screen flex flex-col bg-cream" data-id="element-177">
      <Header data-id="element-178" />

      {/* Hero */}
      <section className="relative h-[65vh] min-h-[480px] flex items-center justify-center overflow-hidden" data-id="element-179">
        <div className="absolute inset-0 z-0" data-id="element-180">
          <img src="https://images.unsplash.com/photo-1500964757637-c85e8a162699?auto=format&fit=crop&q=80" alt="Soft sunlight through leaves" className="w-full h-full object-cover" data-id="element-181" />
          <div className="absolute inset-0 bg-charcoal/35" data-id="element-182"></div>
        </div>

        <div className="relative z-10 text-center px-6 mt-16" data-id="element-183">
          <h1 className="text-5xl md:text-7xl font-serif text-white mb-4 drop-shadow-md" data-id="element-184">
            Journal
          </h1>
          <p className="text-xl md:text-2xl text-white/90 font-serif italic drop-shadow-md max-w-2xl mx-auto" data-id="element-185">
            Reflections from the garden — on stillness, energy, and the small
            wisdoms of everyday life.
          </p>
        </div>

        <div className="absolute bottom-0 left-0 right-0 z-20 pointer-events-none" data-id="element-186">
          <WavyDivider fill="#F8F2E4" data-id="element-187" />
        </div>
      </section>

      {/* Category Intro Cards */}
      <section className="py-20 px-6" data-id="element-188">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8 mb-20" data-id="element-189">
          {categories.map(cat => {
          const count = blogPosts.filter(p => p.category === cat.id).length;
          const isActive = activeCategory === cat.id;
          const Icon = cat.icon;
          return <button key={cat.id} onClick={() => setActiveCategory(isActive ? 'all' : cat.id)} className={`text-left p-10 rounded-2xl border transition-all group ${isActive ? 'bg-sage text-white border-sage shadow-md' : 'bg-white border-cream-200 hover:border-sage hover:shadow-soft'}`} data-id="element-190">
                <div className={`w-12 h-12 rounded-full border flex items-center justify-center mb-6 transition-colors ${isActive ? 'border-white/40 text-white' : 'border-sage/40 text-sage-700 group-hover:border-sage'}`} data-id="element-191">
                  <Icon size={20} strokeWidth={1.25} data-id="element-192" />
                </div>
                <h3 className={`font-serif text-2xl mb-3 ${isActive ? 'text-white' : 'text-charcoal'}`} data-id="element-193">
                  {cat.name}
                </h3>
                <p className={`text-sm font-light leading-relaxed mb-6 ${isActive ? 'text-white/90' : 'text-charcoal-50'}`} data-id="element-194">
                  {cat.description}
                </p>
                <span className={`text-[11px] tracking-[0.3em] uppercase ${isActive ? 'text-white/80' : 'text-sage-700'}`} data-id="element-195">
                  {count} {count === 1 ? 'reflection' : 'reflections'}
                </span>
              </button>;
        })}
        </div>

        {/* All Reflections Button */}
        <div className="max-w-6xl mx-auto flex justify-center mb-16" data-id="element-196">
          <button onClick={() => setActiveCategory('all')} className={`px-7 py-2.5 rounded-full text-xs tracking-[0.2em] uppercase transition-colors ${activeCategory === 'all' ? 'bg-charcoal text-cream' : 'bg-white text-charcoal-50 hover:bg-cream-200 border border-cream-200'}`} data-id="element-197">
            All Reflections
          </button>
        </div>

        {/* Featured + Grid */}
        <div className="max-w-7xl mx-auto" data-id="element-198">
          {featured && <div className="mb-16" data-id="element-199">
              <p className="text-xs tracking-[0.3em] uppercase text-sage-700 mb-4 text-center" data-id="element-200">
                Latest Reflection
              </p>
              <BlogCard post={featured} featured data-id="element-201" />
            </div>}

          {rest.length > 0 && <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-202">
              {rest.map(post => <BlogCard key={post.slug} post={post} data-id="element-203" />)}
            </div>}

          {filteredPosts.length === 0 && <div className="text-center py-20" data-id="element-204">
              <p className="text-charcoal-50 font-serif italic text-xl" data-id="element-205">
                No reflections in this category yet — soon.
              </p>
            </div>}
        </div>
      </section>

      {/* Closing */}
      <section className="relative bg-sage-50 pt-32 pb-20 px-6 mt-12" data-id="element-206">
        <div className="absolute top-0 left-0 right-0 -translate-y-full pointer-events-none" data-id="element-207">
          <WavyDivider fill="#E8EFE7" position="top" data-id="element-208" />
        </div>

        <div className="max-w-2xl mx-auto text-center" data-id="element-209">
          <h2 className="text-3xl md:text-4xl font-serif text-charcoal mb-6" data-id="element-210">
            Stay in the garden
          </h2>
          <p className="text-charcoal-50 font-light leading-relaxed mb-8" data-id="element-211">
            New reflections arrive gently, without urgency. If you'd like them
            to find you, leave your email below.
          </p>
          <form className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-id="element-212">
            <input type="email" placeholder="your@email.com" className="flex-1 px-5 py-3 rounded-full bg-white border border-cream-200 focus:border-sage focus:outline-none text-charcoal font-light" aria-label="Email address" data-id="element-213" />
            <button type="submit" className="bg-sage text-white px-6 py-3 rounded-full text-sm tracking-wide hover:bg-sage-600 transition-colors" data-id="element-214">
              Subscribe
            </button>
          </form>
        </div>
      </section>

      <Footer data-id="element-215" />
    </div>;
}